/**
 * DealsStrip — horizontal rail of discounted products (≥ 15% off) sourced
 * from all nearby stores. Drives revenue by pushing high-margin promos
 * front-and-centre on the home page.
 */
import React, { useMemo } from 'react';
import { SectionHeader, HorizontalRail, Skeleton, css } from '../../design';
import ProductCard from '../products/ProductCard';
import useNearbyProducts from '../../hooks/useNearbyProducts';
import useCartActions from '../../hooks/useCartActions';
import { useNavigate } from 'react-router-dom';

export interface DealsStripProps {
  /** Minimum discount percent threshold. Defaults to 15. */
  minDiscount?: number;
  /** Max number of cards to render. Defaults to 12. */
  limit?: number;
}

const DealsStrip: React.FC<DealsStripProps> = ({ minDiscount = 15, limit = 12 }) => {
  const { loading, products } = useNearbyProducts({ storeLimit: 5, perStore: 30 });
  const cart = useCartActions();
  const navigate = useNavigate();

  const deals = useMemo(() => {
    return products
      .filter((p) => {
        const mrp = p.mrp ?? p.price;
        const eff = p.effective_discount_percent ?? (mrp > p.price ? ((mrp - p.price) / mrp) * 100 : 0);
        return eff >= minDiscount && p.is_available !== false;
      })
      .sort((a, b) => (b.effective_discount_percent ?? 0) - (a.effective_discount_percent ?? 0))
      .slice(0, limit);
  }, [products, minDiscount, limit]);

  if (!loading && deals.length === 0) return null;

  return (
    <section style={{ marginBottom: 24 }}>
      <SectionHeader
        title="🔥 Hot deals near you"
        subtitle={`${deals.length} items at ${minDiscount}%+ off`}
        accent="accent"
        actionLabel={deals.length > 4 ? 'See all' : undefined}
        onAction={() => navigate('/catalog?sort=discount')}
      />
      {loading ? (
        <HorizontalRail ariaLabel="Hot deals">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} style={{ width: 168 }}>
              <Skeleton.ProductCard />
            </div>
          ))}
        </HorizontalRail>
      ) : (
        <HorizontalRail ariaLabel="Hot deals" itemMinWidth={168}>
          {deals.map((p) => (
            <ProductCard
              key={`${p.store_id}:${p.id}`}
              product={p}
              storeName={p.store_name}
              qty={cart.getQty(p)}
              adding={cart.isBusy(p)}
              onAdd={() => cart.add(p)}
              onIncrement={() => cart.increment(p)}
              onDecrement={() => cart.decrement(p)}
              variant="compact"
            />
          ))}
        </HorizontalRail>
      )}
      <div style={{ height: 1 }} aria-hidden />
      <span style={{ display: 'none', color: css.color.textMuted }}>{/* keep theme-aware var ref */}</span>
    </section>
  );
};

export default DealsStrip;

